import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowUpNarrowWide,
  ArrowDownNarrowWide,
  Columns,
  Filter,
  ChevronDown,
  Group,
  Ungroup,
} from 'lucide-react';
import {
  FILTER_OPERATORS,
  emptyColumnFilter,
  columnHasActiveFilter,
} from './columnMenuUtils';
import { columnHeaderStyle, columnHeaderWidthClass } from './columnLayout';

const menuItemClass =
  'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-[#f0f9f4] hover:text-[#46955A]';
const menuItemActiveClass =
  'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm bg-[#f0f9f4] text-[#46955A] font-medium';

/**
 * Column header menu: sort / columns / filter / group.
 * Dropdown is portaled to document.body so scroll/overflow containers do not clip it.
 */
const DataColumnMenu = ({
  column,
  columns,
  sortField,
  sortDirection,
  onSort,
  columnVisibility,
  onToggleColumn,
  filtersByField,
  onApplyFilter,
  onClearFilter,
  groupField,
  onGroupBy,
  onUngroup,
  align = 'left',
  enableGroup = true,
}) => {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState('main'); // main | columns | filter
  const [draftFilter, setDraftFilter] = useState(emptyColumnFilter());
  const [menuPos, setMenuPos] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const field = column.field;
  const hasFilter = columnHasActiveFilter(filtersByField, field);
  const isSorted = sortField === field;
  const isGrouped = groupField === field;
  const showGroup = enableGroup && column.groupable !== false;

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 6,
      left: rect.left,
    });
  }, []);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setPanel('main');
    setMenuPos(null);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updateMenuPosition();

    const onDoc = (e) => {
      const inButton = buttonRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inButton && !inMenu) {
        closeMenu();
      }
    };

    const onReposition = () => updateMenuPosition();

    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);

    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, closeMenu, updateMenuPosition]);

  useEffect(() => {
    if (open && panel === 'filter') {
      setDraftFilter(filtersByField?.[field] || emptyColumnFilter());
    }
  }, [open, panel, field, filtersByField]);

  useEffect(() => {
    if (open) updateMenuPosition();
  }, [open, panel, updateMenuPosition]);

  const toggleMenu = (e) => {
    e.stopPropagation();
    if (open) {
      closeMenu();
      return;
    }
    updateMenuPosition();
    setOpen(true);
  };

  const handleSort = (direction) => {
    onSort?.(field, direction);
    closeMenu();
  };

  const handleApplyFilter = () => {
    onApplyFilter?.(field, draftFilter);
    closeMenu();
  };

  const handleClearFilter = () => {
    onClearFilter?.(field);
    setDraftFilter(emptyColumnFilter());
    closeMenu();
  };

  const updateDraft = (index, key, value) => {
    setDraftFilter((prev) => {
      const filters = prev.filters.map((f, i) =>
        i === index ? { ...f, [key]: value } : f
      );
      return { ...prev, filters };
    });
  };

  const needsValue = (op) => op !== 'isempty' && op !== 'isnotempty';
  const toggleableColumns = (columns || []).filter((c) => c.field && c.hideable !== false);

  const menuContent = open && menuPos ? (
    <div
      ref={menuRef}
      className="fixed z-[9999] w-[260px] rounded-xl border border-gray-200 bg-white p-2 shadow-xl"
      style={{ top: menuPos.top, left: menuPos.left }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {panel === 'main' && (
        <div className="space-y-0.5">
          {column.sortable !== false && (
            <>
              <button type="button" className={menuItemClass} onClick={() => handleSort('asc')}>
                <ArrowUpNarrowWide className="h-4 w-4 text-[#46955A]" />
                Sort A → Z
              </button>
              <button type="button" className={menuItemClass} onClick={() => handleSort('desc')}>
                <ArrowDownNarrowWide className="h-4 w-4 text-[#46955A]" />
                Sort Z → A
              </button>
              <div className="my-1.5 border-t border-gray-100" />
            </>
          )}

          {toggleableColumns.length > 0 && (
            <button type="button" className={menuItemClass} onClick={() => setPanel('columns')}>
              <Columns className="h-4 w-4 text-[#46955A]" />
              Show / hide columns
            </button>
          )}

          {column.filterable !== false && (
            <button
              type="button"
              className={hasFilter ? menuItemActiveClass : menuItemClass}
              onClick={() => setPanel('filter')}
            >
              <Filter className="h-4 w-4 text-[#46955A]" />
              Filter column
            </button>
          )}

          {showGroup && (
            <>
              <div className="my-1.5 border-t border-gray-100" />
              {!isGrouped ? (
                <button
                  type="button"
                  className={menuItemClass}
                  onClick={() => {
                    onGroupBy?.(field);
                    closeMenu();
                  }}
                >
                  <Group className="h-4 w-4 text-[#46955A]" />
                  Group by column
                </button>
              ) : (
                <button
                  type="button"
                  className={menuItemClass}
                  onClick={() => {
                    onUngroup?.();
                    closeMenu();
                  }}
                >
                  <Ungroup className="h-4 w-4 text-[#46955A]" />
                  Clear grouping
                </button>
              )}
            </>
          )}
        </div>
      )}

      {panel === 'columns' && (
        <div>
          <button
            type="button"
            onClick={() => setPanel('main')}
            className="mb-2 text-xs font-medium text-[#46955A] hover:underline"
          >
            ← Back
          </button>
          <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Visible columns
          </p>
          <div className="max-h-56 space-y-1 overflow-y-auto">
            {toggleableColumns.map((col) => {
              const visible = columnVisibility?.[col.field] !== false;
              return (
                <label
                  key={col.field}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-[#f0f9f4]"
                >
                  <input
                    type="checkbox"
                    checked={visible}
                    onChange={() => onToggleColumn?.(col.field)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-[#46955A] focus:ring-[#46955A]"
                  />
                  {col.title}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {panel === 'filter' && (
        <div>
          <button
            type="button"
            onClick={() => setPanel('main')}
            className="mb-2 text-xs font-medium text-[#46955A] hover:underline"
          >
            ← Back
          </button>
          <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Filter · {column.title}
          </p>

          <label className="mb-1 block text-xs text-gray-500">Condition</label>
          <select
            value={draftFilter.filters[0].operator}
            onChange={(e) => updateDraft(0, 'operator', e.target.value)}
            className="mb-2 w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-sm focus:border-[#46955A] focus:bg-white focus:outline-none"
          >
            {FILTER_OPERATORS.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
          {needsValue(draftFilter.filters[0].operator) && (
            <input
              type="text"
              value={draftFilter.filters[0].value}
              onChange={(e) => updateDraft(0, 'value', e.target.value)}
              placeholder="Value…"
              className="mb-3 w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-sm focus:border-[#46955A] focus:bg-white focus:outline-none"
            />
          )}

          <div className="mb-3 flex items-center gap-2">
            <span className="text-xs text-gray-500">Also</span>
            <select
              value={draftFilter.logic}
              onChange={(e) =>
                setDraftFilter((prev) => ({ ...prev, logic: e.target.value }))
              }
              className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-sm focus:border-[#46955A] focus:outline-none"
            >
              <option value="and">And</option>
              <option value="or">Or</option>
            </select>
          </div>

          <select
            value={draftFilter.filters[1].operator}
            onChange={(e) => updateDraft(1, 'operator', e.target.value)}
            className="mb-2 w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-sm focus:border-[#46955A] focus:bg-white focus:outline-none"
          >
            {FILTER_OPERATORS.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
          {needsValue(draftFilter.filters[1].operator) && (
            <input
              type="text"
              value={draftFilter.filters[1].value}
              onChange={(e) => updateDraft(1, 'value', e.target.value)}
              placeholder="Second value… (optional)"
              className="mb-3 w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-sm focus:border-[#46955A] focus:bg-white focus:outline-none"
            />
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleApplyFilter}
              className="flex-1 rounded-lg bg-[#46955A] px-3 py-2 text-sm font-medium text-white hover:bg-[#3d7d4f]"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={handleClearFilter}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  ) : null;

  return (
    <th
      style={columnHeaderStyle(column)}
      className={`data-grid-th relative px-3 py-2.5 align-middle overflow-visible ${
        align === 'center' ? 'text-center' : 'text-left'
      } ${columnHeaderWidthClass(column)} ${column.headerClassName || ''}`}
    >
      <div className={`flex items-center gap-0.5 ${align === 'center' ? 'justify-center' : ''}`}>
        <button
          ref={buttonRef}
          type="button"
          onClick={toggleMenu}
          className={`inline-flex max-w-none items-center gap-1.5 whitespace-nowrap rounded-md px-1.5 py-1 transition-colors hover:bg-[#46955A]/10 ${
            open || hasFilter || isSorted || isGrouped ? 'text-[#46955A]' : 'text-slate-700'
          }`}
          title={`${column.title} — sort, filter, columns`}
        >
          <span className="data-column-title">{column.title}</span>
          {isSorted && (
            <span className="text-[10px] font-semibold">{sortDirection === 'asc' ? '↑' : '↓'}</span>
          )}
          {hasFilter && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#46955A]" />}
          <ChevronDown className={`h-3.5 w-3.5 shrink-0 ${open ? 'text-[#46955A]' : 'text-gray-400'}`} />
        </button>
      </div>
      {menuContent && createPortal(menuContent, document.body)}
    </th>
  );
};

export default DataColumnMenu;
